import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, ImagePlus, X } from "lucide-react";
import { api, imgUrl, formatApiError } from "@/api";

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-flame outline-none py-2.5 text-sm text-bone placeholder:text-faint transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-faint mb-1";

export default function ClientGalleriesManager({ headers }) {
  const [galleries, setGalleries] = useState([]);
  const [photos, setPhotos] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendingFor, setSendingFor] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/client-galleries", { headers });
      setGalleries(data);
      const entries = await Promise.all(
        data.map(async (g) => {
          const r = await api.get(`/client-galleries/${g.id}/photos`, { headers });
          return [g.id, r.data];
        })
      );
      setPhotos(Object.fromEntries(entries));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/client-galleries", { client_name: name, client_email: email, code: code || null }, { headers });
      setName("");
      setEmail("");
      setCode("");
      toast.success("Client gallery created");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadPhotos = async (gid, files) => {
    if (!files?.length) return;
    setUploadingFor(gid);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/client-galleries/${gid}/photos`, fd, { headers });
      setPhotos((p) => ({ ...p, [gid]: [...(p[gid] || []), ...data.added] }));
      toast.success(`${data.count} photo${data.count === 1 ? "" : "s"} added`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingFor(null);
      if (fileRefs.current[gid]) fileRefs.current[gid].value = "";
    }
  };

  const deletePhoto = async (gid, pid) => {
    try {
      await api.delete(`/client-galleries/${gid}/photos/${pid}`, { headers });
      setPhotos((p) => ({ ...p, [gid]: (p[gid] || []).filter((x) => x.id !== pid) }));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendInvite = async (gid) => {
    setSendingFor(gid);
    try {
      await api.post(`/client-galleries/${gid}/send-invite`, {}, { headers });
      toast.success("Access email sent to the client");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingFor(null);
    }
  };

  const deleteGallery = async (gid) => {
    try {
      await api.delete(`/client-galleries/${gid}`, { headers });
      setGalleries((gs) => gs.filter((g) => g.id !== gid));
      toast.success("Gallery deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <form onSubmit={create} className="border border-white/10 bg-coal p-6 sm:p-8 mb-12" data-testid="client-gallery-create-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-6">Set up a client&apos;s photos</p>
        <div className="grid sm:grid-cols-3 gap-x-8 gap-y-6">
          <div>
            <label className={labelCls} htmlFor="cg-name">Client name</label>
            <input id="cg-name" data-testid="cg-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jordan Lee" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="cg-email">Client email</label>
            <input id="cg-email" data-testid="cg-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="cg-code">Access code (blank = auto)</label>
            <input id="cg-code" data-testid="cg-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. SUNSET24" className={`${inputCls} uppercase tracking-[0.2em]`} />
          </div>
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="cg-create-button"
          className="mt-7 bg-flame text-ink px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-flamebright transition-colors disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create client gallery"}
        </button>
      </form>

      {galleries.length === 0 ? (
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-faint" data-testid="cg-empty">No client galleries yet</p>
      ) : (
        <div className="space-y-6">
          {galleries.map((g) => (
            <div key={g.id} className="border border-white/10 bg-coal p-6" data-testid={`cg-card-${g.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                  <p className="font-serif text-2xl text-bone">{g.client_name}</p>
                  <p className="font-mono text-xs text-ash mt-1">{g.client_email}</p>
                  <p className="font-mono text-xs mt-1">
                    <span className="text-faint uppercase tracking-widest">Code</span>{" "}
                    <span className="text-flame tracking-[0.25em]" data-testid={`cg-code-${g.id}`}>{g.code}</span>
                    <span className="text-faint ml-3">{(photos[g.id] ?? []).length} photos</span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid={`cg-send-${g.id}`}
                    onClick={() => sendInvite(g.id)}
                    disabled={sendingFor === g.id}
                    className="flex items-center gap-2 px-4 py-2 border border-white/15 font-mono text-[10px] uppercase tracking-[0.15em] text-ash hover:border-flame hover:text-flame transition-colors disabled:opacity-50"
                  >
                    <Mail size={13} /> {sendingFor === g.id ? "Sending…" : "Email access"}
                  </button>
                  <button
                    data-testid={`cg-delete-${g.id}`}
                    onClick={() => deleteGallery(g.id)}
                    className="flex items-center gap-2 px-4 py-2 border border-white/15 font-mono text-[10px] uppercase tracking-[0.15em] text-faint hover:border-flame hover:text-flame transition-colors"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
              <div
                data-testid={`cg-dropzone-${g.id}`}
                onClick={() => fileRefs.current[g.id]?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  uploadPhotos(g.id, e.dataTransfer.files);
                }}
                className="border border-dashed border-white/20 hover:border-flame/60 transition-colors cursor-pointer px-6 py-8 flex flex-col items-center gap-3 text-center mb-6 bg-ink/40"
              >
                <ImagePlus size={22} className="text-faint" />
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
                  {uploadingFor === g.id ? "Uploading…" : "Drop client photos here or click to browse"}
                </p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-faint/70">JPG / PNG — up to 30 at a time</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid={`cg-upload-${g.id}`}
                  ref={(el) => (fileRefs.current[g.id] = el)}
                  onChange={(e) => uploadPhotos(g.id, e.target.files)}
                />
              </div>
              {(photos[g.id] ?? []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  {(photos[g.id] ?? []).map((p) => (
                    <div key={p.id} className="group relative border border-white/10 overflow-hidden" data-testid={`cg-photo-${p.id}`}>
                      <img src={imgUrl(p.thumb)} alt="" loading="lazy" className="w-full aspect-square object-cover" />
                      <button
                        data-testid={`cg-photo-delete-${p.id}`}
                        onClick={() => deletePhoto(g.id, p.id)}
                        aria-label="Remove photo"
                        className="absolute top-1.5 right-1.5 p-1.5 bg-ink/70 border border-white/15 text-ash hover:text-flame opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
